/**
 * Skin Quiz Core Logic
 * Shared between Dashboard and PDP Banner
 */

window.SkinQuiz = {
    // Constants
    STATUS_COLORS: {
        elite: { hex: '#1a5c68', bg: '#E3EFF3', label: 'ELITE', min: 81 },
        good: { hex: '#2D8B9D', bg: '#E0F2F7', label: 'GOOD', min: 61 },
        needsWork: { hex: '#d97706', bg: '#FEF3C7', label: 'NEEDS WORK', min: 41 },
        atRisk: { hex: '#990000', bg: '#FEE2E2', label: 'AT RISK', min: 21 },
        critical: { hex: '#7f1d1d', bg: '#FEE2E2', label: 'CRITICAL', min: 0 }
    },

    // Storage Keys
    KEYS: {
        ANSWERS: 'skin_quiz_answers',
        COMPLETED: 'skin_quizzes_completed',
        MASTER_SCORE: 'skin_quiz_master_score',
        PREVIOUS_SCORES: 'previous_scores',
        TIER1_TIME: 'tier1_completed_at',
        FRIENDS: 'quiz_friends_progress'
    },

    /**
     * Get status object from a numeric score
     */
    getStatus(score) {
        if (score >= 81) return this.STATUS_COLORS.elite;
        if (score >= 61) return this.STATUS_COLORS.good;
        if (score >= 41) return this.STATUS_COLORS.needsWork;
        if (score >= 21) return this.STATUS_COLORS.atRisk;
        return this.STATUS_COLORS.critical;
    },

    /**
     * Calculate score for a specific quiz
     */
    calculateScore(quizId, answers) {
        const data = window.QUIZ_DATA && window.QUIZ_DATA[quizId];
        if (!data || !answers || answers.length === 0) return 0;

        const mappings = data.mappings;
        let totalRisk = 0;
        let count = 0;

        answers.forEach((ans, qIdx) => {
            // Use fallback to index 0 if specific mapping doesn't exist
            const item = (mappings[qIdx] && mappings[qIdx][ans]) || (mappings[qIdx] && mappings[qIdx][0]);
            if (item) {
                totalRisk += item.r;
                count++;
            }
        });

        if (count === 0) return 0;
        const avgRisk = totalRisk / count;
        return Math.round(Math.max(0, 100 - avgRisk));
    },

    /**
     * Get detailed pillar data (scores + correlations)
     */
    getPillarData(quizId, answers) {
        const data = window.QUIZ_DATA && window.QUIZ_DATA[quizId];
        if (!data || !answers) return null;

        const mappings = data.mappings;
        const questions = data.questions;
        const validAnswers = answers.slice(0, questions.length);

        if (validAnswers.length === 0) return null;

        let totalRisk = 0;
        const correlations = validAnswers.map((ans, qIdx) => {
            const item = (mappings[qIdx] && mappings[qIdx][ans]) || (mappings[qIdx] && mappings[qIdx][0]) || { t: "Analysis", x: "Analyzing data...", r: 5 };
            totalRisk += item.r;
            const subScorePercentage = Math.max(0, 100 - item.r);
            return { title: item.t, text: item.x, score: subScorePercentage };
        });

        const avgRisk = totalRisk / validAnswers.length;
        const score = Math.round(Math.max(0, 100 - avgRisk));

        return { score, correlations };
    },

    /**
     * Data Storage Helpers
     */
    getStoredAnswers() {
        try {
            return JSON.parse(localStorage.getItem(this.KEYS.ANSWERS) || '{}');
        } catch (e) {
            return {};
        }
    },

    saveAnswers(allAnswers) {
        localStorage.setItem(this.KEYS.ANSWERS, JSON.stringify(allAnswers));
        // Reset saved state so they can save new results if they change answers
        localStorage.removeItem('drtowel_quiz_saved');
    },

    getCompletedQuizzes() {
        try {
            return JSON.parse(localStorage.getItem(this.KEYS.COMPLETED) || '[]');
        } catch (e) {
            return [];
        }
    },

    saveCompletedQuizzes(completed) {
        localStorage.setItem(this.KEYS.COMPLETED, JSON.stringify(completed));
    },

    /**
     * Clear all quiz-related data from local storage
     */
    clearAllData() {
        // Clear primary keys
        Object.values(this.KEYS).forEach(key => localStorage.removeItem(key));

        // Clear other related keys found in the codebase
        const otherKeys = [
            'drtowel_quiz_saved',
            'partner_name',
            'friend_name',
            'pdp_user_name',
            'pdp_partner_name',
            'pdp_partner_gender',
            'pdp_shared_partner_score',
            'pdp_shared_partner_answers',
            'pdp_shared_partner_name',
            'pdp_household_user1_name',
            'pdp_household_user1_score',
            'pdp_household_user1_answers',
            'pdp_household_user2_name',
            'pdp_household_user2_score',
            'pdp_household_user2_answers',
            'isCollaborationActive',
            'skin_quiz_progress',
            'pdp_user_answers',
            'pdp_last_partner_name'
        ];
        otherKeys.forEach(key => localStorage.removeItem(key));

        console.log('✨ Skin Quiz local storage cleared.');
    },

    /**
     * Common UI Utilities
     */
    renderStatusBadge(score, includeChevron = false) {
        const status = this.getStatus(score);
        const chevronHtml = includeChevron ? `
      <span class="status-badge-chevron">
        <svg width="10" height="6" viewBox="0 0 10 6" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="status-badge-chevron-icon">
          <path d="M1 1L5 5L9 1"/>
        </svg>
      </span>` : '';

        // Use CSS class with custom property for dynamic color - prevents FOUC from JS style manipulation
        return `
      <div class="status-badge status-badge--dynamic" style="--status-color: ${status.hex};">
        ${status.label} ${chevronHtml}
      </div>
    `;
    },

    /**
     * Get personalized recommendations based on score/answers
     */
    getRecommendations(score, allProducts) {
        // 1. Get Partner's Name
        let partnerName = localStorage.getItem('partner_name') || localStorage.getItem('friend_name') || 'Your partner';

        // 2. Identify Primary Behavior (from answers if available)
        const answers = this.getStoredAnswers();
        const quizAnswers = answers[1] || []; // Hygiene Quiz (ID: 1)

        // Habit Flags (based on QUIZ_DATA mappings)
        const usesBodyTowelOnFace = quizAnswers[0] === 0; // "Normal towel" for face
        const sharedTowel = quizAnswers[6] === 0; // "Shared" towel
        const roughHandling = quizAnswers[5] === 0; // "Firm rubbing/scrubbing"

        // 3. Selection Strategy
        // We want 3 products: 1 primary problem solver, 1 trial (canister), 1 ritual/other.
        let finalRecs = [];

        // --- Product 1: The Primary Problem Solver (Pack of 2) ---
        // We clone to avoid mutating the original global data
        let p1 = null;
        if (usesBodyTowelOnFace || sharedTowel) {
            let base = allProducts.find(p => p.handle === 'best-of-aloe-vera-75-x-150-cm-pack-of-2') || allProducts[0];
            p1 = { ...base, dynamicReason: `${partnerName} currently uses a body towel for their face, which can transfer bacteria. This set helps maintain a dedicated face towel to protect skin health.` };
        } else if (roughHandling) {
            let base = allProducts.find(p => p.handle === 'best-of-banana-bath-towel-pack-of-2-green-combo') || allProducts[2];
            p1 = { ...base, dynamicReason: `${partnerName} may be using a standard towel too roughly. This ultra-soft towel will protect their skin barrier and promote smoother, healthier skin.` };
        } else {
            let base = allProducts[1]; // Best of Alu Vera
            p1 = { ...base, dynamicReason: `These Aloe-infused towels will provide both of you with the gentle care needed for healthy skin—helping to heal and protect your skin barriers.` };
        }
        finalRecs.push(p1);

        // --- Product 2: The Trial (Canister) ---
        let base2 = allProducts.find(p => p.handle.includes('canister')) || allProducts[6];
        let p2 = { ...base2, dynamicReason: `${partnerName} often leaves towels in damp zones where bacteria thrive. This trial unit dries so fast there is 0% room for growth. It's the ideal 'test' to show ${partnerName} how much clearer their skin can be.` };
        finalRecs.push(p2);

        // --- Product 3: The Ritual or High Standard ---
        let p3 = null;
        if (score >= 70) {
            let base3 = allProducts.find(p => p.handle === 'the-ritual-edition-womens-gifting-pack') || allProducts[3];
            p3 = { ...base3, dynamicReason: `${partnerName}’s habits show a strong foundation for elite skin health, but they are currently missing a cohesive system. This complete set provides the clinical standard ${partnerName} deserves.` };
        } else {
            // Default to another Pack of 2 or Gifting Pack
            let base3 = allProducts.find(p => p.handle === 'the-ritual-edition-mens-gifting-pack-chalk-blue') || allProducts[4];
            p3 = { ...base3, dynamicReason: `To isolate and eliminate external microbial triggers, a zero-moisture environment is necessary for you and ${partnerName}. This premium set ensures every touchpoint meets medical-grade criteria.` };
        }
        finalRecs.push(p3);

        return finalRecs;
    },

    /**
     * Compare households and return scenario data
     */
    compareHouseholds(userScore, partnerScore, userAnswers, partnerAnswers) {
        // Need to parse answers if they are likely strings "00120"
        const parse = (ans) => Array.isArray(ans) ? ans : String(ans).split('').map(Number);
        const uAns = parse(userAnswers || []);
        const pAns = parse(partnerAnswers || []);

        // Key Indices (Based on Quiz 13: 0=FaceTowel, 1=Wash, 2=Dry, 3=Skin, 4=Share)
        // Adjust indices if your quiz mapping is different. Assuming Index 4 is sharing.
        const uShares = uAns[4] === 0; // 0 = Yes
        const pShares = pAns[4] === 0;

        let scenario = 'D'; // Default: Perfect Pair
        let reason = '';
        let badge = 'Perfect Pair';

        // Logic Tree
        if (uShares || pShares) {
            scenario = 'B'; // Sharing = Sharing Risk
            reason = "Sharing Risk";
        } else if (Math.abs(userScore - partnerScore) > 30) {
            scenario = 'A'; // Hygiene Gap = Hygiene Mismatch
            reason = "Hygiene Mismatch";
        } else if (userScore < 50 && partnerScore < 50) {
            scenario = 'E'; // Mutual Risk = High Risk
            reason = "High Risk Zone";
        } else if (uAns[2] === 2 && pAns[2] === 2 && userScore > 60) {
            // Both dry in bathroom (Index 2, Value 2 = "In the bathroom")
            scenario = 'C'; // Incubator = Humidity Trap
            reason = "Humidity Trap";
        } else if (userScore >= 80 && partnerScore >= 80) {
            scenario = 'D'; // Elite = Perfect Pair
            reason = "Perfect Pair";
        } else {
            scenario = 'A'; // Fallback to mismatch
            reason = "Hygiene Mismatch";
        }

        return { scenario, reason };
    },

    /**
     * Get Household specific recommendations
     */
    getHouseholdRecs(scenario, allProducts, userName, partnerName) {
        let finalRecs = [];
        let p1, p2;

        // Name Helpers
        // Prefer passed names, fallback to storage, then defaults
        let uName = userName || localStorage.getItem('pdp_user_name') || 'You';
        let pName = partnerName || localStorage.getItem('pdp_partner_name') || localStorage.getItem('pdp_shared_partner_name') || localStorage.getItem('partner_name') || 'Partner';

        const cap = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
        const U = (uName.toLowerCase() === 'you') ? 'You' : cap(uName);
        const P = cap(pName);

        // Helper to find prod
        const find = (h) => allProducts.find(p => p.handle.includes(h)) || allProducts[0];

        switch (scenario) {
            case 'A': // Hygiene Gap
                p1 = { ...find('aloevera-double'), dynamicReason: `To bridge the hygiene gap, ${P} needs a towel that dries 3x faster than cotton. This prevents the moisture-loving bacteria currently affecting their score.` };
                p2 = { ...find('banana-bath'), dynamicReason: `If ${P} isn't washing towels daily, this antibacterial fabric naturally repels pathogens, ensuring their skin touches a sterile surface every time.` };
                break;
            case 'B': // Sharing
                p1 = { ...find('aloe-vera'), dynamicReason: `Sharing towels transfers ${P}'s skin flora directly to your face (and vice versa). This dedicated set physically breaks that chain of infection.` };
                p2 = { ...find('banana-bath'), dynamicReason: `${U} needs a distinct, pathogen-resistant surface. This ensures that even if one of you falls behind on laundry, the other remains protected.` };
                break;
            case 'C': // Incubator
                p1 = { ...find('aloevera-double'), dynamicReason: `Bathroom humidity turns standard towels into petri dishes. This Double Cloth weave airs out 40% faster, starving bacteria of the moisture they need to breed.` };
                p2 = { ...find('banana-double'), dynamicReason: `Prevents the 'incubator effect' in your shared bathroom. The rapid-dry fiber ensures ${P} is drying their face with a clinically clean towel, not a damp one.` };
                break;
            case 'D': // Elite
                p1 = { ...find('womens'), dynamicReason: `To maintain your elite hygiene score, you need fabrics that don't degrade. This medical-grade weave prevents micro-tears where bacteria usually hide.` };
                p2 = { ...find('mens'), dynamicReason: `Matches the household's high standard. It actively prevents environmental contaminants from compromising the strong skin barrier ${P} has built.` };
                break;
            case 'E': // Reset
                p1 = { ...find('alu-vera'), dynamicReason: `Your current towels are likely a biological risk factor. This SkinShield technology continually neutralizes pathogens on contact to break the cycle of irritation for ${P}.` };
                p2 = { ...find('aloe-vera'), dynamicReason: `A mandatory reset. These hypoallergenic towels eliminate the bio-film buildup of your old linens, ensuring ${U} avoids re-introducing bacteria to clean skin.` };
                break;
            default:
                p1 = { ...find('aloe-vera'), dynamicReason: `Standard cotton traps dirt. This upgraded fiber releases debris during washing, ensuring a true clean for ${P}.` };
                p2 = { ...find('aloevera-double'), dynamicReason: `Bacteria thrives in travel/gym bags. This self-drying technology ensures ${U} never wipes their face with a stagnant, damp cloth.` };
        }

        finalRecs.push(p1);
        finalRecs.push(p2);
        return finalRecs;
    }
};
